/**
 * Settings functions for Cloudflare Workers
 */

import { createClient } from "@supabase/supabase-js";
import type { PriceGeneratorConditions } from "../../shared/price-generator";
import {
  getDefaultPriceGeneratorConditions,
  mergePriceGeneratorConditionsPatch,
  normalizePriceGeneratorConditions,
} from "../../shared/price-generator";

export interface RouteGeneratorFixedStage {
  stageId: string;
  triggerOnEnter: boolean;
  triggerOnExit: boolean;
  maxExtrasPerFix?: number; // Optional: max number of extra points per fix point (0 = no extras)
  vehicleId?: string | null; // Optional: if set, only this vehicle can visit deals in this stage
}

export interface RouteGeneratorVehicleDriver {
  vehicleId: string;
  driverName: string;
  percentage?: number;
}

/** One surface option for configurator: Povrch with allowed motivs (M/L) and colors */
export interface ConfiguratorProductOption {
  surface: string;
  label?: string;
  motivs: string[];
  colors: { code: string; name: string; colorHex?: string; overlayFilename?: string }[];
}

/** Daňový doklad után: Zmluva o dielo generálás + e-mail (daňový + Zmluva melléklet) */
export interface AutomationOnTaxDocumentZmluvaEmail {
  enabled: boolean;
}

/** Számla befizetése (úhrada webhook / callback_payment) után: deal stage (subpipeline a deal-en változatlan marad) */
export interface AutomationOnInvoicePaid {
  /** CRM subpipeline (pipeline) id → sablon stage id; a webhook a deal saját subpipeline-jában név alapján párosít */
  stagesBySubpipelineId?: Record<string, string> | null;
  /** @deprecated egyszeri választás — migrálás után stagesBySubpipelineId */
  stageId?: string | null;
  pipelineId?: string | null;
}

/** „Dokončiť meranie” / bemérés befejezése után: subpipeline → sablon stage */
export interface AutomationOnMeasureComplete {
  stagesBySubpipelineId?: Record<string, string> | null;
}

export interface AutomationClosingDateOnStage {
  enabled: boolean;
  /** Stage id -> offset days (0..7) */
  offsetDaysByStageId: Record<string, number>;
  /** How long to suppress automation after a manual change */
  cooldownMinutes: number;
}

/** AI: megjegyzés alapú lead besorolás → CRM stage (FB + konfigurátor). */
export interface AiLeadClassificationCategory {
  /** Belső AI / napló kulcs (UUID); a UI nem szerkeszti. */
  key: string;
  label: string;
  description: string;
  /** Normalizált szakasz-ujjlenyomat; a deal saját subpipeline-jában így párosítjuk a stage id-t. */
  stageCanonical: string;
  /** @deprecated korábbi mentés — ha nincs stageCanonical, a worker egy ideig ebből derivál */
  stageId?: string | null;
  /** If true (default), the system sends the quote PDF email to the customer when a lead is classified into this category. */
  sendQuoteEmail?: boolean;
}

export interface AiLeadClassificationSettings {
  enabled: boolean;
  /** 3–5 kategória (+ külön üres megjegyzés sor a Settings UI-ban). */
  categories: AiLeadClassificationCategory[];
  /** Üres megjegyzés: ugyanaz a canonical, subpipeline változatlan */
  emptyNote: { stageCanonical: string; stageId?: string | null; sendQuoteEmail?: boolean };
}

/** Resolve the sendQuoteEmail flag for a given AI classification category. Returns true (send) by default for backward compat. */
export function shouldSendQuoteEmailForCategory(
  cfg: AiLeadClassificationSettings | null | undefined,
  categoryKey: string | null | undefined
): boolean {
  if (!cfg || !categoryKey) return true;
  if (categoryKey === "empty_note") return cfg.emptyNote?.sendQuoteEmail !== false;
  const cat = cfg.categories.find((c) => c.key.trim() === categoryKey);
  if (!cat) return true;
  return cat.sendQuoteEmail !== false;
}

export interface AutomationsSettings {
  onTaxDocumentZmluvaEmail?: AutomationOnTaxDocumentZmluvaEmail;
  onInvoicePaid?: AutomationOnInvoicePaid;
  onMeasureComplete?: AutomationOnMeasureComplete;
  closingDateOnStage?: AutomationClosingDateOnStage;
  aiLeadClassification?: AiLeadClassificationSettings | null;
}

/**
 * Sablon stage id lekérése a deal subpipeline-jához: map[dealPipelineId], majd legacy onInvoicePaid mezők.
 */
export function getInvoicePaidTemplateStageId(
  onInvoicePaid: AutomationOnInvoicePaid | null | undefined,
  dealPipelineId: string | null | undefined
): string | null {
  const pid = dealPipelineId != null ? String(dealPipelineId).trim() : "";
  if (!pid) return null;
  const map = onInvoicePaid?.stagesBySubpipelineId;
  if (map && typeof map === "object") {
    const v = map[pid];
    if (v != null && String(v).trim()) return String(v).trim();
  }
  const leg = onInvoicePaid;
  if (leg?.stageId != null && String(leg.stageId).trim()) {
    const sid = String(leg.stageId).trim();
    if (leg.pipelineId != null && String(leg.pipelineId) === pid) return sid;
    if (!map || Object.keys(map).length === 0) return sid;
  }
  return null;
}

/** Bemérés befejezése: subpipeline → sablon stage; régi measureTargetStageId ha nincs map. */
export function getMeasureCompleteTemplateStageId(
  onMeasure: AutomationOnMeasureComplete | null | undefined,
  dealPipelineId: string | null | undefined,
  legacyMeasureTargetStageId?: string | null
): string | null {
  const pid = dealPipelineId != null ? String(dealPipelineId).trim() : "";
  if (!pid) return null;
  const map = onMeasure?.stagesBySubpipelineId;
  if (map && typeof map === "object") {
    const v = map[pid];
    if (v != null && String(v).trim()) return String(v).trim();
  }
  if (legacyMeasureTargetStageId != null && String(legacyMeasureTargetStageId).trim()) {
    if (!map || Object.keys(map).length === 0) return String(legacyMeasureTargetStageId).trim();
  }
  return null;
}

export interface Settings {
  manualsFolderId?: string | null;
  measureTargetStageId?: string | null;
  routeGeneratorFixedStages?: RouteGeneratorFixedStage[];
  routeGeneratorQuoteStageId?: string | null; // Keep for backward compatibility, but we'll use stage name
  routeGeneratorQuoteStageName?: string | null; // Stage name (not ID) - matches all stages with this name across all pipelines
  routeGeneratorMeasurementRounds?: { minPointsPerDay: number; maxPointsPerDay: number } | 'auto';
  routeGeneratorUseDocumentReconstruction?: boolean; // Kniha jázd style: merge visits from superfaktura_documents (default false)
  routeGeneratorBlockOtherVehiclesOnFixPointDays?: boolean; // On original fix point days, block other vehicles from getting routes (default false)
  routeGeneratorVehicleDrivers?: RouteGeneratorVehicleDriver[]; // Kniha jázd: vehicle -> driver mapping
  supplierStampImageUrl?: string | null;
  /** Árgenerátor Podmienky: toggles and tolerance/discount params */
  priceGeneratorConditions?: PriceGeneratorConditions | null;
  /** Konfigurátor: Povrch → Prelis (motivs), Povrch → színek (colors) */
  configuratorProductOptions?: ConfiguratorProductOption[] | null;
  /** Konfigurátor: motív sorrend (pl. ["m","l"]). Ha üres, fallback: m, l. */
  configuratorMotivOrder?: string[] | null;
  /** Access multi-product configurator options (separate from sectional) */
  accessConfiguratorOptions?: import("../../shared/access-configurator").AccessConfiguratorOptions | null;
  /** Roger Technology series rules for access configurator */
  accessRogerSeriesRules?: import("../../shared/access-configurator").AccessRogerSeriesRule[] | null;
  /** Automatizálások (Beállítások): pl. daňový doklad után Zmluva + e-mail */
  automations?: AutomationsSettings | null;
  /** Stage ID for deals created from Facebook Lead Ads webhook. If null, fallback to env or configurator default. */
  facebookLeadStageId?: string | null;
  /** Default surface/prelis for Facebook lead price calculation (e.g. "hladký"). Falls back to "standard". */
  facebookLeadDefaultSurface?: string | null;
  updatedAt?: string;
}

async function initSupabaseFromEnv(env: {
  SUPABASE_URL: string;
  SUPABASE_ANON_KEY: string;
  SUPABASE_SERVICE_ROLE_KEY?: string;
  SUPABASE_SERVICE_KEY?: string;
}) {
  if (!env.SUPABASE_URL || !env.SUPABASE_ANON_KEY) {
    throw new Error("Supabase credentials not configured");
  }

  const supabaseKey =
    env.SUPABASE_SERVICE_ROLE_KEY || env.SUPABASE_SERVICE_KEY || env.SUPABASE_ANON_KEY;

  return createClient(env.SUPABASE_URL, supabaseKey);
}

export async function getSettings(
  env: {
    SUPABASE_URL: string;
    SUPABASE_ANON_KEY: string;
    SUPABASE_SERVICE_ROLE_KEY?: string;
    SUPABASE_SERVICE_KEY?: string;
  }
): Promise<Settings> {
  try {
    if (!env.SUPABASE_URL || !env.SUPABASE_ANON_KEY) {
      console.error("[Settings] Missing Supabase credentials");
      return { manualsFolderId: null, measureTargetStageId: null };
    }

    // Use direct REST API call - use id='main' instead of key='main' to avoid schema cache issues
    const supabaseKey =
      env.SUPABASE_SERVICE_ROLE_KEY || env.SUPABASE_SERVICE_KEY || env.SUPABASE_ANON_KEY;
    const restUrl = `${env.SUPABASE_URL}/rest/v1/settings`;
    
    // Try to get by id first, if that fails, try by key
    console.log(`[SETTINGS-LIB] Fetching settings via REST API: ${restUrl}?id=eq.main`);
    
    let response = await fetch(`${restUrl}?id=eq.main`, {
      method: 'GET',
      headers: {
        'apikey': supabaseKey,
        'Authorization': `Bearer ${supabaseKey}`,
        'Content-Type': 'application/json',
      },
    });
    
    // If id doesn't work, try key
    if (!response.ok) {
      console.log(`[SETTINGS-LIB] Trying with key parameter: ${restUrl}?key=eq.main`);
      response = await fetch(`${restUrl}?key=eq.main`, {
        method: 'GET',
        headers: {
          'apikey': supabaseKey,
          'Authorization': `Bearer ${supabaseKey}`,
          'Content-Type': 'application/json',
        },
      });
    }
    
    // Settings table has direct columns, not a value JSONB column
    // Structure: id, manualsFolderId, measureTargetStageId, routeGeneratorFixedStages, routeGeneratorQuoteStageId, routeGeneratorMeasurementRounds, routeGeneratorUseDocumentReconstruction, supplierStampImageUrl, createdAt, updatedAt
    let data: { 
      id: string; 
      manualsFolderId?: string | null; 
      measureTargetStageId?: string | null;
      routeGeneratorFixedStages?: any;
      routeGeneratorQuoteStageId?: string | null;
      routeGeneratorQuoteStageName?: string | null;
      routeGeneratorMeasurementRounds?: any;
      routeGeneratorUseDocumentReconstruction?: boolean;
      routeGeneratorBlockOtherVehiclesOnFixPointDays?: boolean;
      routeGeneratorVehicleDrivers?: RouteGeneratorVehicleDriver[];
      supplierStampImageUrl?: string | null;
      priceGeneratorConditions?: PriceGeneratorConditions | null;
      configuratorProductOptions?: ConfiguratorProductOption[] | null;
      configuratorMotivOrder?: string[] | null;
      accessConfiguratorOptions?: import("../../shared/access-configurator").AccessConfiguratorOptions | null;
      accessRogerSeriesRules?: import("../../shared/access-configurator").AccessRogerSeriesRule[] | null;
      automations?: AutomationsSettings | null;
      facebookLeadStageId?: string | null;
      facebookLeadDefaultSurface?: string | null;
      updatedAt?: string;
    } | null = null;
    
    if (response.ok) {
      const result = await response.json() as Array<{
        id: string;
        manualsFolderId?: string | null;
        measureTargetStageId?: string | null;
        routeGeneratorFixedStages?: any;
        routeGeneratorQuoteStageId?: string | null;
        routeGeneratorQuoteStageName?: string | null;
        routeGeneratorMeasurementRounds?: any;
        routeGeneratorUseDocumentReconstruction?: boolean;
        supplierStampImageUrl?: string | null;
        updatedAt?: string;
      }>;
      if (result && result.length > 0) {
        data = result[0];
        console.log(`[SETTINGS-LIB] Settings fetched successfully`);
      } else {
        console.log(`[SETTINGS-LIB] No settings found in database`);
      }
    } else {
      const errorText = await response.text();
      console.error(`[SETTINGS-LIB] Get settings error (${response.status}):`, errorText);
    }

    if (data) {
      // Parse routeGeneratorMeasurementRounds - it might be JSONB or 'auto' string
      let measurementRounds: { minPointsPerDay: number; maxPointsPerDay: number } | 'auto' = 'auto';
      if (data.routeGeneratorMeasurementRounds) {
        if (typeof data.routeGeneratorMeasurementRounds === 'string' && data.routeGeneratorMeasurementRounds === 'auto') {
          measurementRounds = 'auto';
        } else if (typeof data.routeGeneratorMeasurementRounds === 'object') {
          measurementRounds = data.routeGeneratorMeasurementRounds as { minPointsPerDay: number; maxPointsPerDay: number };
        }
      }
      
      return {
        manualsFolderId: data.manualsFolderId || null,
        measureTargetStageId: data.measureTargetStageId || null,
        routeGeneratorFixedStages: Array.isArray(data.routeGeneratorFixedStages) ? data.routeGeneratorFixedStages : (data.routeGeneratorFixedStages ? [data.routeGeneratorFixedStages] : []),
        routeGeneratorQuoteStageId: data.routeGeneratorQuoteStageId || null,
        routeGeneratorQuoteStageName: data.routeGeneratorQuoteStageName || null,
        routeGeneratorMeasurementRounds: measurementRounds,
        routeGeneratorUseDocumentReconstruction: data.routeGeneratorUseDocumentReconstruction ?? false,
        routeGeneratorBlockOtherVehiclesOnFixPointDays: data.routeGeneratorBlockOtherVehiclesOnFixPointDays ?? false,
        routeGeneratorVehicleDrivers: Array.isArray(data.routeGeneratorVehicleDrivers) ? data.routeGeneratorVehicleDrivers : [],
        supplierStampImageUrl: data.supplierStampImageUrl || null,
        priceGeneratorConditions: normalizePriceGeneratorConditions(data.priceGeneratorConditions),
        configuratorProductOptions: Array.isArray(data.configuratorProductOptions) ? data.configuratorProductOptions : [],
        configuratorMotivOrder: Array.isArray(data.configuratorMotivOrder) ? data.configuratorMotivOrder : null,
        accessConfiguratorOptions: data.accessConfiguratorOptions ?? null,
        accessRogerSeriesRules: Array.isArray(data.accessRogerSeriesRules) ? data.accessRogerSeriesRules : null,
        automations: data.automations ?? {},
        facebookLeadStageId: data.facebookLeadStageId ?? null,
        facebookLeadDefaultSurface: data.facebookLeadDefaultSurface ?? null,
        updatedAt: data.updatedAt || undefined,
      };
    }

    // Return defaults if no settings found
    return {
      manualsFolderId: null,
      measureTargetStageId: null,
      routeGeneratorFixedStages: [],
      routeGeneratorQuoteStageId: null,
      routeGeneratorQuoteStageName: null,
      routeGeneratorMeasurementRounds: 'auto',
      routeGeneratorUseDocumentReconstruction: false,
      routeGeneratorVehicleDrivers: [],
      supplierStampImageUrl: null,
      priceGeneratorConditions: getDefaultPriceGeneratorConditions(),
      configuratorProductOptions: [],
      configuratorMotivOrder: null,
      accessConfiguratorOptions: null,
      accessRogerSeriesRules: null,
      automations: {},
      facebookLeadStageId: null,
      facebookLeadDefaultSurface: null,
    };
  } catch (error) {
    console.error("[Settings] Error getting settings:", error);
    return {
      manualsFolderId: null,
      measureTargetStageId: null,
      routeGeneratorFixedStages: [],
      routeGeneratorQuoteStageId: null,
      routeGeneratorQuoteStageName: null,
      routeGeneratorMeasurementRounds: 'auto',
      routeGeneratorUseDocumentReconstruction: false,
      routeGeneratorVehicleDrivers: [],
      supplierStampImageUrl: null,
      priceGeneratorConditions: getDefaultPriceGeneratorConditions(),
      configuratorProductOptions: [],
      configuratorMotivOrder: null,
      accessConfiguratorOptions: null,
      accessRogerSeriesRules: null,
      automations: {},
      facebookLeadStageId: null,
      facebookLeadDefaultSurface: null,
    };
  }
}

export async function updateSettings(
  env: {
    SUPABASE_URL: string;
    SUPABASE_ANON_KEY: string;
    SUPABASE_SERVICE_ROLE_KEY?: string;
    SUPABASE_SERVICE_KEY?: string;
  },
  updates: Partial<Settings>
): Promise<Settings> {
  console.log(`[SETTINGS-LIB] === updateSettings CALLED ===`);
  console.log(`[SETTINGS-LIB] Updates received:`, {
    manualsFolderId: updates.manualsFolderId,
    measureTargetStageId: updates.measureTargetStageId,
    routeGeneratorFixedStages: Array.isArray(updates.routeGeneratorFixedStages) ? `${updates.routeGeneratorFixedStages.length} items` : updates.routeGeneratorFixedStages,
    routeGeneratorQuoteStageId: updates.routeGeneratorQuoteStageId,
    routeGeneratorMeasurementRounds: updates.routeGeneratorMeasurementRounds,
  });
  
  try {
    if (!env.SUPABASE_URL || !env.SUPABASE_ANON_KEY) {
      throw new Error("Supabase credentials not configured");
    }

    const db = await initSupabaseFromEnv(env);
    
    // Get current settings
    const current = await getSettings(env);
    console.log(`[SETTINGS-LIB] Current settings:`, {
      manualsFolderId: current.manualsFolderId,
      measureTargetStageId: current.measureTargetStageId,
      routeGeneratorFixedStages: Array.isArray(current.routeGeneratorFixedStages) ? `${current.routeGeneratorFixedStages.length} items` : current.routeGeneratorFixedStages,
      routeGeneratorQuoteStageId: current.routeGeneratorQuoteStageId,
      routeGeneratorMeasurementRounds: current.routeGeneratorMeasurementRounds,
    });
    
    // Merge updates (shallow-merge automations so partial API payloads do not drop sibling keys)
    const { automations: automationsPatch, priceGeneratorConditions: conditionsPatch, ...updatesRest } =
      updates;
    const mergedAutomations: AutomationsSettings | null | undefined =
      automationsPatch !== undefined
        ? { ...(current.automations ?? {}), ...automationsPatch }
        : current.automations;

    const mergedConditions =
      conditionsPatch !== undefined
        ? mergePriceGeneratorConditionsPatch(current.priceGeneratorConditions, conditionsPatch)
        : normalizePriceGeneratorConditions(current.priceGeneratorConditions);

    const updated: Settings = {
      ...current,
      ...updatesRest,
      automations: mergedAutomations,
      priceGeneratorConditions: mergedConditions,
      updatedAt: new Date().toISOString(),
    };

    // Upsert settings using direct REST API call to bypass PostgREST schema cache issues
    // Don't include updated_at - let database DEFAULT handle it (schema cache doesn't have it)
    const upsertData = {
      key: "main",
      value: {
        manualsFolderId: updated.manualsFolderId,
        measureTargetStageId: updated.measureTargetStageId,
        routeGeneratorFixedStages: updated.routeGeneratorFixedStages,
        routeGeneratorQuoteStageId: updated.routeGeneratorQuoteStageId,
        routeGeneratorQuoteStageName: updated.routeGeneratorQuoteStageName,
        routeGeneratorMeasurementRounds: updated.routeGeneratorMeasurementRounds,
        routeGeneratorUseDocumentReconstruction: updated.routeGeneratorUseDocumentReconstruction,
        routeGeneratorBlockOtherVehiclesOnFixPointDays: updated.routeGeneratorBlockOtherVehiclesOnFixPointDays,
        routeGeneratorVehicleDrivers: updated.routeGeneratorVehicleDrivers ?? [],
        supplierStampImageUrl: updated.supplierStampImageUrl,
        priceGeneratorConditions: updated.priceGeneratorConditions ?? undefined,
        configuratorProductOptions: updated.configuratorProductOptions ?? undefined,
        configuratorMotivOrder: updated.configuratorMotivOrder ?? undefined,
        accessConfiguratorOptions: updated.accessConfiguratorOptions ?? undefined,
        accessRogerSeriesRules: updated.accessRogerSeriesRules ?? undefined,
        automations: updated.automations ?? undefined,
        facebookLeadStageId: updated.facebookLeadStageId ?? undefined,
        facebookLeadDefaultSurface: updated.facebookLeadDefaultSurface ?? undefined,
      },
      // Don't include updated_at - PostgREST schema cache doesn't have it
      // The database DEFAULT NOW() will handle it automatically
    };
    
    console.log(`[SETTINGS-LIB] Upserting to database via REST API:`, {
      key: upsertData.key,
      value: {
        ...upsertData.value,
        routeGeneratorFixedStages: Array.isArray(upsertData.value.routeGeneratorFixedStages) 
          ? `${upsertData.value.routeGeneratorFixedStages.length} items` 
          : upsertData.value.routeGeneratorFixedStages,
      },
      // updated_at not included - database DEFAULT will handle it
    });
    
    // Use direct REST API call instead of Supabase client to bypass schema cache
    const supabaseKey =
      env.SUPABASE_SERVICE_ROLE_KEY || env.SUPABASE_SERVICE_KEY || env.SUPABASE_ANON_KEY;
    const restUrl = `${env.SUPABASE_URL}/rest/v1/settings`;
    
    // Use RPC function to update settings - this bypasses PostgREST schema cache issues
    // The function is defined in migration 028_create_update_settings_function.sql
    console.log(`[SETTINGS-LIB] Updating settings using RPC function`);
    const rpcUrl = `${env.SUPABASE_URL}/rest/v1/rpc/update_settings_value`;
    const rpcResponse = await fetch(rpcUrl, {
      method: 'POST',
      headers: {
        'apikey': supabaseKey,
        'Authorization': `Bearer ${supabaseKey}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation',
      },
      body: JSON.stringify({
        p_key: upsertData.key,
        p_value: upsertData.value,
      }),
    });
    
    if (!rpcResponse.ok) {
      const errorText = await rpcResponse.text();
      console.error(`[SETTINGS-LIB] ✗ RPC update error (${rpcResponse.status}):`, errorText);
      throw new Error(`Database update failed: ${rpcResponse.status} - ${errorText}`);
    }
    
    console.log(`[SETTINGS-LIB] ✓ RPC update successful`);
    
    const fixedStagesDisplay = updated.routeGeneratorFixedStages !== undefined
      ? (Array.isArray(updated.routeGeneratorFixedStages) ? `${updated.routeGeneratorFixedStages.length} items` : String(updated.routeGeneratorFixedStages))
      : 'undefined';
    
    console.log(`[SETTINGS-LIB] Returning updated settings:`, {
      manualsFolderId: updated.manualsFolderId,
      measureTargetStageId: updated.measureTargetStageId,
      routeGeneratorFixedStages: fixedStagesDisplay,
      routeGeneratorQuoteStageId: updated.routeGeneratorQuoteStageId,
      routeGeneratorMeasurementRounds: updated.routeGeneratorMeasurementRounds,
      supplierStampImageUrl: updated.supplierStampImageUrl ? 'set' : 'null',
    });

    return updated;
  } catch (error) {
    console.error(`[SETTINGS-LIB] ✗ Error updating settings:`, error);
    console.error(`[SETTINGS-LIB] ✗ Error details:`, {
      name: error instanceof Error ? error.name : typeof error,
      message: error instanceof Error ? error.message : 'No message',
      stack: error instanceof Error ? error.stack : 'No stack',
      fullError: error instanceof Error ? error.toString() : JSON.stringify(error, Object.getOwnPropertyNames(error)),
    });
    throw error;
  }
}

/** Short string for audit log (settings diff). */
export function summarizeSettingValue(value: unknown, maxLen = 80): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") {
    if (!value) return "—";
    if (value.length > maxLen) return `${value.slice(0, maxLen)}…`;
    return value;
  }
  if (Array.isArray(value)) return `[${value.length} items]`;
  if (typeof value === "object") {
    const n = Object.keys(value as object).length;
    return `{${n} keys}`;
  }
  return String(value).slice(0, maxLen);
}

const SHALLOW_DIFF_SETTING_FIELDS = new Set<string>(["automations", "priceGeneratorConditions"]);

function expandShallowObjectDiff(
  field: string,
  fromVal: unknown,
  toVal: unknown
): Array<{ field: string; from: unknown; to: unknown }> | null {
  const b =
    fromVal !== null && typeof fromVal === "object" && !Array.isArray(fromVal)
      ? (fromVal as Record<string, unknown>)
      : null;
  const a =
    toVal !== null && typeof toVal === "object" && !Array.isArray(toVal)
      ? (toVal as Record<string, unknown>)
      : null;
  if (!b && !a) return null;
  const bk = b ? Object.keys(b) : [];
  const ak = a ? Object.keys(a) : [];
  const keySet = new Set([...bk, ...ak]);
  const rows: Array<{ field: string; from: unknown; to: unknown }> = [];
  for (const k of keySet) {
    const vb = b ? b[k] : undefined;
    const va = a ? a[k] : undefined;
    let same = false;
    try {
      same = JSON.stringify(vb) === JSON.stringify(va);
    } catch {
      same = vb === va;
    }
    if (same) continue;
    rows.push({
      field: `${field}.${k}`,
      from: summarizeSettingValue(vb),
      to: summarizeSettingValue(va),
    });
  }
  return rows.length ? rows : null;
}

/**
 * Build `changed` rows for audit: only keys present in `patch` (non-undefined) where value differs after merge.
 */
export function computeSettingsAuditChanged(
  before: Settings,
  after: Settings,
  patch: Partial<Settings>
): Array<{ field: string; from: unknown; to: unknown }> {
  const keys = Object.keys(patch).filter((k) => (patch as Record<string, unknown>)[k] !== undefined);
  const out: Array<{ field: string; from: unknown; to: unknown }> = [];
  for (const key of keys) {
    const ko = key as keyof Settings;
    const fromVal = before[ko];
    const toVal = after[ko];
    let same = false;
    try {
      same = JSON.stringify(fromVal) === JSON.stringify(toVal);
    } catch {
      same = fromVal === toVal;
    }
    if (same) continue;
    if (SHALLOW_DIFF_SETTING_FIELDS.has(key)) {
      const expanded = expandShallowObjectDiff(key, fromVal, toVal);
      if (expanded) {
        out.push(...expanded);
        continue;
      }
    }
    out.push({
      field: key,
      from: summarizeSettingValue(fromVal),
      to: summarizeSettingValue(toVal),
    });
  }
  return out;
}

